// Parser GS1 compacto para flujos de recepción (checklist GS1: escaneo de
// SSCC en Recepciones y conteo ciego con escáner).
// Subconjunto de AIs del parser completo de Ingresos/Egresos: (00) (01) (02)
// (10) (17) (21) (37). Maneja prefijos AIM del lector, FNC1 real (ASCII 29)
// y el "0029" literal que envía el DS2278.
import { isValidGS1Key } from './gs1Validation'

const GS = String.fromCharCode(29)

/**
 * Quita el prefijo AIM Code ID del lector (]C1, ]d2, ]E0, …). Acepta también
 * la variante con "+" — el lector emula teclado US y con layout ES/ES-LA la
 * tecla de "]" produce "+" (]C1 llega como +C1). Usar en TODO punto de
 * escaneo que compare o parsee códigos.
 */
export function stripAIM(code) {
  return String(code || '').trim().replace(/^[\]+][A-Za-z][0-9A-Za-z]/, '')
}

// AIs soportados: fijos con largo, variables con máximo
const AI_DEFS = [
  { key: '00', len: 18, fixed: true, field: 'sscc' },
  { key: '01', len: 14, fixed: true, field: 'gtin' },
  { key: '02', len: 14, fixed: true, field: 'content_gtin' },
  { key: '17', len: 6, fixed: true, field: 'expiry' },
  { key: '11', len: 6, fixed: true, field: 'production' },
  { key: '13', len: 6, fixed: true, field: 'pack' },
  { key: '15', len: 6, fixed: true, field: 'best_before' },
  { key: '10', fixed: false, max: 20, field: 'lot' },
  { key: '21', fixed: false, max: 20, field: 'serial' },
  { key: '37', fixed: false, max: 8, field: 'count' }
]

function normalize(raw) {
  let s = stripAIM(raw) // prefijo AIM del lector (]C1 o +C1 según teclado)
  s = s.replace(/[()]/g, '')
  s = s.replace(/\u001d/g, GS).replace(/<FNC1>|<GS>|\|/gi, GS)
  // DS2278: FNC1 como texto "0029" entre un valor y el siguiente AI conocido
  s = s.replace(/(\d{3,30})0029(?=(10|11|13|15|17|21|37))/g, `$1${GS}`)
  return s
}

function yymmddToISO(v) {
  if (!/^\d{6}$/.test(v)) return null
  const mm = Number(v.slice(2, 4))
  if (mm < 1 || mm > 12) return null
  const dd = v.slice(4, 6) === '00' ? '01' : v.slice(4, 6)
  return `20${v.slice(0, 2)}-${v.slice(2, 4)}-${dd}`
}

/**
 * Parsea un escaneo GS1 y devuelve los campos reconocidos:
 * { sscc, gtin, content_gtin, lot, serial, expiry (ISO), count (int), raw }
 * Los AIs desconocidos detienen el parseo (igual que el parser principal).
 */
export function parseGS1Scan(raw) {
  const s = normalize(raw)
  const out = { raw: s }

  // Clave suelta sin AI: SSCC de 18 o GTIN de 8/12/13/14 con checksum válido
  if (/^\d+$/.test(s)) {
    if (s.length === 18 && isValidGS1Key(s, [18])) {
      out.sscc = s
      return out
    }
    if ([8, 12, 13, 14].includes(s.length) && isValidGS1Key(s)) {
      out.gtin = s.padStart(14, '0')
      return out
    }
  }

  let i = 0
  while (i < s.length) {
    if (s[i] === GS) { i++; continue }
    const def = AI_DEFS.find(d => s.startsWith(d.key, i))
    if (!def) break
    i += def.key.length
    let value
    if (def.fixed) {
      value = s.slice(i, i + def.len)
      i += def.len
    } else {
      let j = i
      while (j < s.length && j - i < def.max && s[j] !== GS) j++
      value = s.slice(i, j)
      i = j
    }
    out[def.field] = value
  }

  if (out.expiry) out.expiry = yymmddToISO(out.expiry)
  if (out.count) out.count = parseInt(out.count, 10) || null
  return out
}

/**
 * Extrae y valida un SSCC de un escaneo. Devuelve
 * { sscc } o { error } con mensaje legible.
 */
export function extractSSCC(raw) {
  const parsed = parseGS1Scan(raw)
  const s = normalize(raw)
  // (00) presente pero aún no validado, o 18 dígitos pelados con checksum malo
  const candidate = parsed.sscc || (s.startsWith('00') && s.length >= 20 ? s.slice(2, 20) : (/^\d{18}$/.test(s) ? s : null))
  if (!candidate) {
    return { error: 'El código escaneado no contiene un SSCC (00)' }
  }
  if (!/^\d{18}$/.test(candidate)) {
    return { error: `SSCC debe tener 18 dígitos numéricos (recibido: "${candidate}")` }
  }
  if (!isValidGS1Key(candidate, [18])) {
    return { error: `Dígito verificador del SSCC incorrecto (${candidate})` }
  }
  return { sscc: candidate }
}

/** Compara un GTIN escaneado con el barcode/gtin de un producto (padding a 14). */
export function gtinMatches(scanned, productCode) {
  if (!scanned || !productCode) return false
  const a = String(scanned).replace(/\D/g, '')
  const b = String(productCode).replace(/\D/g, '')
  if (!a || !b) return false
  return a.padStart(14, '0') === b.padStart(14, '0')
}
